<script setup lang="ts">
    import { computed, ref } from 'vue';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import { ATTRIBUTE_NAME_MAX_LENGTH, validateAttributeName } from './attributeNameValidation.ts';

    const props = defineProps<{
        currentName: string;
        otherNames: string[];
    }>();

    const emit = defineEmits<{
        confirm: [value: string];
        cancel: [];
    }>();

    const value = ref(props.currentName);

    const errorMessage = computed<string | null>(() => validateAttributeName(value.value, props.otherNames));

    const isUnchanged = computed(() => value.value.trim() === props.currentName.trim());

    const canSubmit = computed(() => !errorMessage.value && !isUnchanged.value);

    function onSubmit() {
        if (canSubmit.value) {
            emit('confirm', value.value.trim());
        }
    }

    function onCancel() {
        emit('cancel');
    }
</script>

<template>
    <modal-base
        @on-cancel="onCancel">
        <template #modal-header>
            Rename attribute '{{ props.currentName }}'
        </template>
        <template #modal-body>
            <b-message
                type="is-danger"
                has-icon>
                Renaming an attribute changes how it is exposed wherever the name is used as an
                identifier: API/JSON field names, generated library view columns, exports
                (Excel/CSV), schemas, saved queries or filters that reference it. Direct aliases are migrated automatically, but references from other libraries (for example
                relation/CodeRef aliases) and external consumers relying on the old name will break.
                Stored values are preserved.
                Renaming an in use identity attribute will be blocked if it would cause the identity column order to change.
                The rename is applied when you save your changes.
            </b-message>
            <b-field
                label="New name"
                :type="errorMessage ? 'is-danger' : undefined"
                :message="errorMessage">
                <b-input
                    v-model="value"
                    :maxlength="ATTRIBUTE_NAME_MAX_LENGTH"
                    @keyup.enter="onSubmit" />
            </b-field>
        </template>
        <template #modal-footer>
            <div class="buttons">
                <b-button
                    label="Cancel"
                    @click="onCancel" />
                <b-button
                    label="Ok"
                    type="is-primary"
                    :disabled="!canSubmit"
                    @click="onSubmit" />
            </div>
        </template>
    </modal-base>
</template>
